import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import { MousePointerClick, ExternalLink, Target, ListFilter, Maximize2, Newspaper } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

// Cards for /admin/analytics: which buttons people press, where they leave to, and which traffic sources turn
// into sent forms. Every card is a short chart with a dropdown; the full breakdown opens in a pop-out.

const ORANGE = "hsl(16,86%,49%)";
const GREY = "hsl(0,0%,38%)";
const BLUE = "hsl(200,55%,55%)";
const TOOLTIP = { background: "#1a1a1a", border: "1px solid #444", borderRadius: 8, fontSize: 12 };

// ---------- names ----------

// data-track label -> [display name, group]. Anything not listed falls back to a tidied label.
const BUTTONS = {
  "hero-cta-primary": ["Our Story (hero)", "Home page"],
  "hero-cta-secondary": ["Talk to us (hero)", "Home page"],
  "hero-pillar-heartOfHouse": ["Heart of House pillar", "Home page"],
  "hero-pillar-familyMeals": ["Family Meals pillar", "Home page"],
  "hero-pillar-atHome": ["Mise at Home pillar", "Home page"],
  "platform-info-heartOfHouse": ["Heart of House: Info", "Home page"],
  "platform-info-familyMeals": ["Family Meals: Info", "Home page"],
  "platform-info-atHome": ["Mise at Home: Info", "Home page"],
  "platform-site-heartOfHouse": ["Heart of House: site", "Home page"],
  "platform-site-familyMeals": ["Family Meals: site", "Home page"],
  "platform-site-atHome": ["Mise at Home: site", "Home page"],
  "answer-chip-heart-of-house": ["Heart of House chip", "Home page"],
  "answer-chip-family-meals": ["Family Meals chip", "Home page"],
  "answer-chip-at-home": ["Mise at Home chip", "Home page"],
  "cta-band": ["Contact band (bottom)", "Home page"],

  "hoh-hero-logo": ["Logo to heartofhouse.ca", "Heart of House"],
  "hoh-book-demo": ["Book a Demo (hero)", "Heart of House"],
  "hoh-pilot-hero": ["Founding pilot (hero)", "Heart of House"],
  "hoh-pilot-bottom": ["Founding pilot (bottom)", "Heart of House"],
  "hoh-tab-0": ["Tab: Food cost", "Heart of House"],
  "hoh-tab-1": ["Tab: Menu", "Heart of House"],
  "hoh-tab-2": ["Tab: Suppliers & inventory", "Heart of House"],
  "hoh-tab-3": ["Tab: Labour & month-end", "Heart of House"],
  "hoh-demo-logo": ["Demo page: logo to app", "Heart of House"],
  "hoh-demo-email-support": ["Demo page: email support", "Heart of House"],
  "hoh-pilot-apply-hero": ["Pilot: Apply (top)", "Heart of House"],
  "hoh-pilot-apply-band": ["Pilot: Apply (bottom)", "Heart of House"],
  "hoh-pilot-email-sean": ["Pilot: Email Sean (top)", "Heart of House"],
  "hoh-pilot-email-sean-band": ["Pilot: Email Sean (bottom)", "Heart of House"],
  "hoh-pilot-email-sean-card": ["Apply page: Email Sean", "Heart of House"],

  "family-meals-join": ["Join the network", "Family Meals"],
  "family-meals-site": ["Family Meals: visit site", "Family Meals"],
  "family-meals-door-0": ["Door: I'm a restaurant", "Family Meals"],
  "family-meals-door-1": ["Door: I'm a nonprofit", "Family Meals"],
  "family-meals-door-2": ["Door: Find help", "Family Meals"],
  "family-meals-press-0": ["Press: CTV News", "Family Meals"],
  "family-meals-press-1": ["Press: Calgary Herald", "Family Meals"],
  "fm-coming-soon-supplier": ["Coming soon: restaurant", "Family Meals"],
  "fm-coming-soon-distributor": ["Coming soon: nonprofit", "Family Meals"],

  "at-home-play-hero": ["Google Play (top)", "Mise at Home"],
  "at-home-play-bottom": ["Google Play (bottom)", "Mise at Home"],
  "at-home-site": ["At Home: visit site", "Mise at Home"],

  "about-contact": ["About: Contact", "Contact"],
  "contact-direct-email": ["Contact: support email", "Contact"],
  "contact-marketing-email": ["Contact: marketing email", "Contact"],
  "contact-social-instagram": ["Contact: Instagram", "Social"],
  "contact-social-tiktok": ["Contact: TikTok", "Social"],
  "contact-social-facebook": ["Contact: Facebook", "Social"],
  "footer-social-instagram": ["Footer: Instagram", "Social"],
  "footer-social-tiktok": ["Footer: TikTok", "Social"],
  "footer-social-facebook": ["Footer: Facebook", "Social"],

  "header-cta": ["Header: Pilot interest", "Navigation"],
  "mobile-cta": ["Mobile menu: Pilot interest", "Navigation"],
  "nav-nav-home": ["Menu: Home", "Navigation"],
  "nav-nav-at-home": ["Menu: Households", "Navigation"],
  "nav-nav-family-meals": ["Menu: Food Service & Nonprofits", "Navigation"],
  "nav-nav-heart-of-house": ["Menu: Restaurant Operators", "Navigation"],
  "nav-nav-about": ["Menu: About", "Navigation"],
  "nav-nav-contact": ["Menu: Contact", "Navigation"],
  "lang-en": ["Language: EN", "Navigation"],
  "lang-fr": ["Language: FR", "Navigation"],
  "footer-at-home": ["Footer: Mise at Home", "Navigation"],
  "footer-family-meals": ["Footer: Family Meals", "Navigation"],
  "footer-heart-of-house": ["Footer: Heart of House", "Navigation"],
  "footer-about": ["Footer: About", "Navigation"],
  "footer-contact": ["Footer: Contact", "Navigation"],
  "footer-media": ["Footer: Media", "Navigation"],
  "nav-nav-media": ["Nav: Media", "Navigation"],
  "media-press-email": ["Media: press email", "Contact"],
  "media-filter-all": ["Media filter: All", "Media"],
  "media-filter-press": ["Media filter: In the press", "Media"],
  "media-filter-post": ["Media filter: From Mise", "Media"],
  "family-meals-press-all": ["Family Meals: All press", "Family Meals"],
  "footer-marketing-email": ["Footer: marketing email", "Contact"],
};
const BUTTON_GROUPS = ["Heart of House", "Family Meals", "Mise at Home", "Media", "Home page", "Contact", "Social", "Navigation"];

const tidy = (s) => (s || "unknown").replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
// Story cards on /media are tracked as media-press-<id> / media-post-<id>; a post's button as media-post-cta-<slug>.
const mediaButton = (label) => {
  if (/^media-post-cta-/.test(label)) return ["Media post: button", "Media"];
  if (/^media-(press|post)-/.test(label)) return ["Media: story card", "Media"];
  return null;
};
const buttonName = (label) => (BUTTONS[label] || mediaButton(label) || [tidy(label)])[0];
const buttonGroup = (label) => (BUTTONS[label] || mediaButton(label) || [null, "Other"])[1];

// Outgoing link target ("host/path" or "mailto:x") -> [destination, group]
const destination = (target) => {
  const t = target || "";
  if (t.startsWith("mailto:")) return [`Email ${t.slice(7)}`, "Email"];
  const host = t.split("/")[0];
  if (host === "heartofhouse.ca" || host.endsWith(".heartofhouse.ca")) return ["Heart of House app", "Our sites"];
  if (host === "misefamilymeals.ca" || host.endsWith(".misefamilymeals.ca")) return ["Family Meals site", "Our sites"];
  if (host === "play.google.com") return ["Google Play (At Home)", "Our sites"];
  if (host === "misehome.ca" || host.endsWith(".misehome.ca")) return ["Mise at Home site", "Our sites"];
  if (/instagram\.com$/.test(host)) return ["Instagram", "Social"];
  if (/tiktok\.com$/.test(host)) return ["TikTok", "Social"];
  if (/facebook\.com$/.test(host)) return ["Facebook", "Social"];
  if (/ctvnews\.ca$/.test(host)) return ["CTV News article", "Press"];
  if (/calgaryherald\.com$/.test(host)) return ["Calgary Herald article", "Press"];
  if (/globalnews\.ca$/.test(host)) return ["Global News video", "Press"];
  if (/linkedin\.com$/.test(host)) return ["LinkedIn", "Social"];
  return [host || t, "Other"];
};
const OUT_GROUPS = ["Our sites", "Social", "Email", "Press", "Other"];

export const SOURCE_NAMES = {
  direct: "Direct", organic: "Search", social: "Social", referral: "Other sites", email: "Email", paid: "Paid",
};
const sourceName = (s) => SOURCE_NAMES[s] || tidy(s);

const FORM_NAMES = {
  contact: "Contact", "hoh-demo": "Book a demo", "hoh-pilot": "Pilot application",
  "family-meals-join": "Family Meals join", waitlist: "At Home waitlist",
};
const formName = (f) => FORM_NAMES[f] || tidy(f);
const pageName = (p) => (p === "/" ? "Home" : p);
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);

// ---------- building blocks ----------

const Select = ({ value, onChange, options, testId }) => (
  <label className="relative inline-flex items-center">
    <ListFilter size={13} className="pointer-events-none absolute left-2.5 text-white/45" />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none rounded-lg border border-[#444444] bg-[#1a1a1a] py-1.5 pl-7 pr-7 text-xs text-white/85 outline-none hover:border-[#666] focus:border-[#E8490F]"
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <span className="pointer-events-none absolute right-2.5 text-[9px] text-white/45">▼</span>
  </label>
);

const CardHead = ({ icon: Icon, title, sub, children }) => (
  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
    <div>
      <h3 className="flex items-center gap-2 text-lg font-semibold">
        <Icon size={18} className="text-[#E8490F]" />
        {title}
      </h3>
      {sub && <p className="mt-0.5 text-xs text-white/45">{sub}</p>}
    </div>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const MoreButton = ({ onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-lg border border-[#444444] bg-[#1a1a1a] px-2.5 py-1.5 text-xs text-white/75 hover:border-[#E8490F] hover:text-white"
    data-testid={testId}
  >
    <Maximize2 size={12} />
    See all
  </button>
);

const Empty = ({ children }) => (
  <div className="flex h-56 items-center justify-center rounded-xl border border-dashed border-[#333] text-sm text-white/40">
    {children}
  </div>
);

// Top rows as a horizontal bar chart; height follows the row count so it never needs scrolling.
const TopBars = ({ rows, color = ORANGE, name = "Clicks" }) => (
  <div style={{ height: Math.max(rows.length * 30 + 16, 96) }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 34, top: 0, bottom: 0 }}>
        <XAxis type="number" hide allowDecimals={false} />
        <YAxis type="category" dataKey="name" width={178} tick={{ fill: "#ccc", fontSize: 11.5 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={TOOLTIP} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
        <Bar dataKey="count" name={name} fill={color} radius={[0, 4, 4, 0]} barSize={14}>
          <LabelList dataKey="count" position="right" fill="#ddd" fontSize={11} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// Pop-out: rows grouped under headings, each with a slim inline bar and the pages it happened on.
const BreakdownDialog = ({ open, onOpenChange, title, groups, testId }) => {
  const max = Math.max(1, ...groups.flatMap((g) => g.rows.map((r) => r.count)));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-[#333] bg-[#141414] text-white" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {groups.map((g) => (
            <div key={g.name} className="mb-5 last:mb-0">
              <p className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E8490F]">
                {g.name}
                <span className="text-white/40">{g.total}</span>
              </p>
              <ul className="space-y-1.5">
                {g.rows.map((r) => (
                  <li key={r.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-white/90">{r.name}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-[#262626]">
                        <div className="h-1.5 rounded-full bg-[#E8490F]" style={{ width: `${(r.count / max) * 100}%` }} />
                      </div>
                      {r.pages && <p className="mt-1 truncate text-[11px] text-white/40">{r.pages}</p>}
                    </div>
                    <span className="font-medium tabular-nums">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Sum rows by key; keep a short "from" list of the pages each one happened on.
const rollUp = (items, keyOf, nameOf, groupOf) => {
  const map = new Map();
  for (const it of items) {
    const key = keyOf(it);
    const row = map.get(key) || { key, name: nameOf(it), group: groupOf(it), count: 0, pageCounts: {} };
    row.count += it.count;
    row.pageCounts[it.page] = (row.pageCounts[it.page] || 0) + it.count;
    map.set(key, row);
  }
  return [...map.values()]
    .map((r) => ({
      ...r,
      pages: "from " + Object.entries(r.pageCounts).sort((a, b) => b[1] - a[1]).map(([p, n]) => `${pageName(p)} (${n})`).join(", "),
    }))
    .sort((a, b) => b.count - a.count);
};

const toGroups = (rows, order) =>
  order
    .map((name) => {
      const gr = rows.filter((r) => r.group === name);
      return { name, rows: gr, total: gr.reduce((s, r) => s + r.count, 0) };
    })
    .filter((g) => g.rows.length);

// ---------- cards ----------

export const ButtonsCard = ({ clicks = [] }) => {
  const [group, setGroup] = useState("all");
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => rollUp(clicks, (c) => c.label, (c) => buttonName(c.label), (c) => buttonGroup(c.label)), [clicks]);
  const order = [...BUTTON_GROUPS, "Other"];
  const shown = rows.filter((r) => group === "all" || r.group === group).slice(0, 8);

  return (
    <div className="mise-card p-6" data-testid="analytics-buttons">
      <CardHead icon={MousePointerClick} title="Button clicks" sub="Top 8 for the dates above">
        <Select
          value={group}
          onChange={setGroup}
          testId="analytics-buttons-filter"
          options={[{ value: "all", label: "All buttons" }, ...order.map((g) => ({ value: g, label: g }))]}
        />
        {rows.length > 0 && <MoreButton onClick={() => setOpen(true)} testId="analytics-buttons-more" />}
      </CardHead>
      {shown.length ? <TopBars rows={shown} /> : <Empty>No button clicks yet</Empty>}
      <BreakdownDialog open={open} onOpenChange={setOpen} title="Every button, by section" groups={toGroups(rows, order)} testId="analytics-buttons-dialog" />
    </div>
  );
};

export const OutboundCard = ({ outbound = [] }) => {
  const [group, setGroup] = useState("Our sites");
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => rollUp(outbound, (o) => destination(o.target)[0], (o) => destination(o.target)[0], (o) => destination(o.target)[1]), [outbound]);
  const shown = rows.filter((r) => group === "all" || r.group === group).slice(0, 8);
  const ours = rows.filter((r) => r.group === "Our sites").reduce((s, r) => s + r.count, 0);

  return (
    <div className="mise-card p-6" data-testid="analytics-outbound">
      <CardHead icon={ExternalLink} title="Outgoing traffic" sub={`${ours} visits sent to our other sites`}>
        <Select
          value={group}
          onChange={setGroup}
          testId="analytics-outbound-filter"
          options={[{ value: "all", label: "Everywhere" }, ...OUT_GROUPS.map((g) => ({ value: g, label: g }))]}
        />
        {rows.length > 0 && <MoreButton onClick={() => setOpen(true)} testId="analytics-outbound-more" />}
      </CardHead>
      {shown.length ? <TopBars rows={shown} color={BLUE} name="Visits" /> : <Empty>No outgoing clicks yet</Empty>}
      <BreakdownDialog open={open} onOpenChange={setOpen} title="Where visitors went, and from which page" groups={toGroups(rows, OUT_GROUPS)} testId="analytics-outbound-dialog" />
    </div>
  );
};

export const ConversionsCard = ({ conversions = [], timeline = [] }) => {
  const [form, setForm] = useState("all");
  const forms = useMemo(() => {
    const set = new Set();
    conversions.forEach((c) => Object.keys(c.forms || {}).forEach((f) => set.add(f)));
    return [...set];
  }, [conversions]);

  const data = conversions.map((c) => {
    const converted = form === "all" ? c.converted : (c.forms || {})[form] || 0;
    return { name: sourceName(c.source), sessions: c.sessions, converted, rate: pct(converted, c.sessions) };
  });
  const sessions = data.reduce((s, r) => s + r.sessions, 0);
  const converted = data.reduce((s, r) => s + r.converted, 0);
  const best = [...data].filter((r) => r.converted > 0).sort((a, b) => b.rate - a.rate)[0];

  return (
    <div className="mise-card p-6 mb-6" data-testid="analytics-conversions">
      <CardHead icon={Target} title="Conversions by traffic source" sub="A conversion is a sent form. Each visit counts under the source it arrived from.">
        <Select
          value={form}
          onChange={setForm}
          testId="analytics-conversions-filter"
          options={[{ value: "all", label: "All forms" }, ...forms.map((f) => ({ value: f, label: formName(f) }))]}
        />
      </CardHead>

      <div className="mb-5 grid grid-cols-3 gap-3">
        {[
          ["Forms sent", converted],
          ["Conversion rate", `${pct(converted, sessions)}%`],
          ["Best source", best ? `${best.name} · ${best.rate}%` : "None yet"],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-[#2c2c2c] bg-[#1a1a1a] px-4 py-3">
            <p className="text-[11px] uppercase tracking-wider text-white/45">{label}</p>
            <p className="mt-1 truncate text-xl font-semibold tracking-tight">{value}</p>
          </div>
        ))}
      </div>

      {sessions === 0 ? (
        <Empty>No visits in this range yet</Empty>
      ) : (
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <p className="mb-2 text-xs text-white/50">Visits and conversions per source</p>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 18, right: 8, left: -14, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#ccc", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#888", fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="sessions" name="Visits" fill={GREY} radius={[4, 4, 0, 0]} barSize={26} />
                  <Bar dataKey="converted" name="Conversions" fill={ORANGE} radius={[4, 4, 0, 0]} barSize={26}>
                    <LabelList dataKey="rate" position="top" fill="#E8490F" fontSize={11} formatter={(v) => (v ? `${v}%` : "")} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lg:col-span-2">
            <p className="mb-2 text-xs text-white/50">All forms sent per day</p>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline} margin={{ top: 18, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} minTickGap={12} />
                  <YAxis tick={{ fill: "#888", fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="conversions" name="Forms sent" fill={ORANGE} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Started vs sent for each form over the whole range.
export const FormFunnelCard = ({ formActions = [] }) => {
  const byForm = {};
  for (const f of formActions) {
    const [form, step] = (f.label || "").split(":");
    if (!form || (step !== "start" && step !== "submit")) continue;
    byForm[form] = byForm[form] || { name: formName(form), started: 0, sent: 0 };
    byForm[form][step === "start" ? "started" : "sent"] += f.count;
  }
  const data = Object.values(byForm).sort((a, b) => b.started + b.sent - (a.started + a.sent));
  if (!data.length) return null;
  return (
    <div className="mise-card p-6 mb-6" data-testid="analytics-forms">
      <CardHead icon={Target} title="Forms: started vs sent" sub="For the dates above" />
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 18, right: 8, left: -14, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: "#ccc", fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#888", fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="started" name="Started" fill={GREY} radius={[4, 4, 0, 0]} barSize={24} />
            <Bar dataKey="sent" name="Sent" fill={ORANGE} radius={[4, 4, 0, 0]} barSize={24}>
              <LabelList dataKey="sent" position="top" fill="#ddd" fontSize={11} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ---------- Media stories ----------

// The same "host/path" key the tracker records for an outgoing link.
const outboundKey = (href) => {
  try {
    const url = new URL(href);
    return url.hostname.replace(/^www\./, "") + url.pathname.replace(/\/$/, "");
  } catch {
    return null;
  }
};

const fmtDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return y ? new Date(y, m - 1, d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : iso;
};

// How many times each story on /media was opened. A post counts views of its own page (from the Media page,
// a shared link or anywhere else); a press story counts clicks out to the outlet from any page on the site.
export const MediaStoriesCard = ({ stories = [], mediaPages = [], outbound = [] }) => {
  const [kind, setKind] = useState("all");
  const mediaViews = mediaPages.find((m) => m.page === "/media")?.views || 0;

  const rows = useMemo(() => {
    const views = Object.fromEntries(mediaPages.map((m) => [m.page, m.views]));
    return stories
      .map((s) => {
        if (s.kind === "post") {
          return { ...s, opens: views[`/media/${s.slug}`] || 0, fromMedia: null };
        }
        const key = outboundKey(s.url);
        const hits = outbound.filter((o) => o.target === key);
        const opens = hits.reduce((n, o) => n + o.count, 0);
        const fromMedia = hits.filter((o) => o.page === "/media").reduce((n, o) => n + o.count, 0);
        return { ...s, opens, fromMedia };
      })
      .sort((a, b) => b.opens - a.opens || (a.date < b.date ? 1 : -1));
  }, [stories, mediaPages, outbound]);

  const shown = kind === "all" ? rows : rows.filter((r) => r.kind === kind);
  const totalOpens = rows.reduce((n, r) => n + r.opens, 0);
  const max = Math.max(1, ...rows.map((r) => r.opens));

  return (
    <div className="mise-card p-6 mb-6" data-testid="analytics-media">
      <CardHead icon={Newspaper} title="Media stories" sub="Times each story was opened, for the dates above">
        <Select
          value={kind}
          onChange={setKind}
          testId="analytics-media-filter"
          options={[
            { value: "all", label: "All stories" },
            { value: "press", label: "In the press" },
            { value: "post", label: "From Mise" },
          ]}
        />
      </CardHead>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:max-w-md">
        <div className="rounded-xl border border-[#333] bg-[#1a1a1a] px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.14em] text-white/45">Media page views</p>
          <p className="mt-1 text-2xl font-semibold" data-testid="analytics-media-views">{mediaViews.toLocaleString("en-CA")}</p>
        </div>
        <div className="rounded-xl border border-[#333] bg-[#1a1a1a] px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.14em] text-white/45">Story opens</p>
          <p className="mt-1 text-2xl font-semibold" data-testid="analytics-media-opens">{totalOpens.toLocaleString("en-CA")}</p>
        </div>
      </div>

      {shown.length === 0 ? (
        <Empty>No stories yet</Empty>
      ) : (
        <ul className="divide-y divide-[#2a2a2a]" data-testid="analytics-media-list">
          {shown.map((r) => (
            <li key={r.id} className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_280px] sm:items-center sm:gap-6">
              <div className="min-w-0">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/45">
                  <span className={r.kind === "post" ? "text-[#E8490F]" : "text-[hsl(200,55%,65%)]"}>
                    {r.kind === "post" ? "From Mise" : r.outlet}
                  </span>
                  {" · "}
                  {fmtDate(r.date)}
                </p>
                <p className="mt-1 truncate text-sm text-white/90" title={r.title}>{r.title}</p>
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#262626]">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(r.opens / max) * 100}%`, background: r.kind === "post" ? ORANGE : BLUE }}
                    />
                  </div>
                  <span className="w-10 text-right text-sm font-semibold tabular-nums">{r.opens}</span>
                </div>
                <p className="mt-1 text-[11px] text-white/40">
                  {r.kind === "post"
                    ? "views of the post page"
                    : `clicks to ${r.outlet}${r.opens ? ` · ${r.fromMedia} from /media` : ""}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
